// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Height and weight as the CareRx enrollment captures them (ASP ticket #5, 2026-09-30). People
// measure height in feet and inches; OwlRx stores inches (and weight in ounces). These values drive
// dosing and print on a signed form, so anything outside a plausible human range is refused
// rather than stored.

export const HEIGHT_IN = { min: 12, max: 108 } as const   // 1 ft – 9 ft
export const WEIGHT_LB = { min: 1, max: 1000 } as const

export type Parsed = { ok: true; value: number | null } | { ok: false; error: string }

const num = (s: string) => (s.trim() === '' ? null : Number(s))

/** Feet + inches → total inches. Both blank → null (not entered). */
export function inchesFromFeetInches(feet: string, inches: string): Parsed {
  const f = num(feet), i = num(inches)
  if (f === null && i === null) return { ok: true, value: null }
  if ((f !== null && (!Number.isFinite(f) || f < 0)) || (i !== null && (!Number.isFinite(i) || i < 0)))
    return { ok: false, error: 'Height must be a positive number.' }
  if (i !== null && i >= 12) return { ok: false, error: 'Inches must be under 12 — use the feet box.' }
  const total = (f ?? 0) * 12 + (i ?? 0)
  if (total < HEIGHT_IN.min || total > HEIGHT_IN.max)
    return { ok: false, error: `Height must be between ${HEIGHT_IN.min / 12} ft and ${HEIGHT_IN.max / 12} ft.` }
  return { ok: true, value: Math.round(total * 10) / 10 }
}

/** Total inches → the two boxes. */
export function feetInchesFromInches(total: number | null | undefined): { feet: string; inches: string } {
  if (!total || total <= 0) return { feet: '', inches: '' }
  const feet = Math.floor(total / 12)
  const inches = Math.round((total - feet * 12) * 10) / 10
  return { feet: String(feet), inches: String(inches) }
}

/** Pounds → ounces (stored unit). Blank → null. */
export function ouncesFromPounds(pounds: string): Parsed {
  const lb = num(pounds)
  if (lb === null) return { ok: true, value: null }
  if (!Number.isFinite(lb) || lb < WEIGHT_LB.min || lb > WEIGHT_LB.max)
    return { ok: false, error: `Weight must be between ${WEIGHT_LB.min} and ${WEIGHT_LB.max} lb.` }
  return { ok: true, value: Math.round(lb * 16) }
}

/** Ounces → the pounds box. */
export function poundsFromOunces(oz: number | null | undefined): string {
  return !oz || oz <= 0 ? '' : String(Math.round((oz / 16) * 10) / 10)
}

/**
 * The height/weight part of an enrollment save: only what the operator changed. An emptied field is
 * sent as an explicit clear (review 2026-10-02, S10) — a missing value means "not supplied", so a
 * wrong measurement could not be removed and kept printing on the form a prescriber signs.
 */
export function measurementPayload(h: { dirty: boolean; parsed: Parsed }, w: { dirty: boolean; parsed: Parsed }):
    { height_inches?: number; weight_ounces?: number; clear_height?: true; clear_weight?: true } {
  const one = (m: { dirty: boolean; parsed: Parsed }) => !m.dirty || !m.parsed.ok ? 'skip' : m.parsed.value == null ? 'clear' : m.parsed.value
  const hv = one(h), wv = one(w)
  return {
    ...(typeof hv === 'number' ? { height_inches: hv } : hv === 'clear' ? { clear_height: true as const } : {}),
    ...(typeof wv === 'number' ? { weight_ounces: wv } : wv === 'clear' ? { clear_weight: true as const } : {}),
  }
}

// ── D-CRX-88: the pharmacy's weight unit (SaaS admin → CareRx → Weight unit) ────────────────────────
// lb is the default and today's behaviour, byte for byte. Storage never changes: weight stays OUNCES (PioneerRx's
// unit; dbo.patients is never written). Only what a screen shows and what the operator types convert, and they
// convert HERE — every CareRx screen calls these, never its own math.

/** The unit one entry box is in. */
export type WeightBoxUnit = 'lb' | 'kg'
/** The pharmacy's setting: lb (default, today), kg, or both (kg first, lb beside it — owner 2026-10-22). */
export type WeightUnit = WeightBoxUnit | 'both'
export const WEIGHT_UNIT_KEY = 'carerx.weight_unit'
/** Exact definitions: 1 lb = 0.45359237 kg; 1 oz = 1/16 lb. */
export const KG_PER_LB = 0.45359237
export const KG_PER_OZ = 0.028349523125
/** The kg box accepts what the lb box does (1–1000 lb) as it shows in kg: 1000 lb shows as 453.6 kg and must be
 *  re-enterable; 453.6 kg stores as 16000 oz = 1000 lb exactly. */
export const WEIGHT_KG = { min: 0.5, max: 453.6 } as const

/** Anything but 'kg' or 'both' (unset, unknown, an older server) is lb — today. */
export function parseWeightUnit(v: unknown): WeightUnit {
  return v === 'kg' || v === 'both' ? v : 'lb'
}

const kgOneDecimal = (oz: number) => Math.round(oz * KG_PER_OZ * 10) / 10

/** Kilograms → ounces (stored unit), whole ounces — the rounding an lb entry gets. Blank → null. */
export function ouncesFromKilograms(kilograms: string): Parsed {
  const kg = num(kilograms)
  if (kg === null) return { ok: true, value: null }
  if (!Number.isFinite(kg) || kg < WEIGHT_KG.min || kg > WEIGHT_KG.max)
    return { ok: false, error: `Weight must be between ${WEIGHT_KG.min} and ${WEIGHT_KG.max} kg.` }
  return { ok: true, value: Math.round(kg / KG_PER_OZ) }
}

/** Ounces → the kilograms box (one decimal). */
export function kilogramsFromOunces(oz: number | null | undefined): string {
  return !oz || oz <= 0 ? '' : String(kgOneDecimal(oz))
}

/** Stored ounces → one weight box in its unit. lb = today's box exactly. */
export function weightBoxFromOunces(oz: number | null | undefined, unit: WeightBoxUnit): string {
  return unit === 'kg' ? kilogramsFromOunces(oz) : poundsFromOunces(oz)
}

/**
 * The weight box → ounces, in the unit the operator typed in. lb = today's ouncesFromPounds exactly. kg: re-typing
 * the value the box was seeded with keeps the seeded ounces, so a kg round trip never moves a stored weight (a
 * tenth of a kilogram is 3.5 oz; the shown value alone cannot carry the ounces back).
 */
export function ouncesFromWeightBox(text: string, unit: WeightBoxUnit, seededOz?: number | null): Parsed {
  if (unit !== 'kg') return ouncesFromPounds(text)
  const parsed = ouncesFromKilograms(text)
  if (parsed.ok && parsed.value !== null && seededOz && seededOz > 0 && Number(text) === kgOneDecimal(seededOz))
    return { ok: true, value: seededOz }
  return parsed
}

/** A stored weight as a screen shows it, unit included: "154.0 lb" (today), "69.9 kg", or both "69.9 kg (154.0 lb)". */
export function formatWeight(oz: number, unit: WeightUnit): string {
  const kg = `${kgOneDecimal(oz).toFixed(1)} kg`, lb = `${(oz / 16).toFixed(1)} lb`
  return unit === 'kg' ? kg : unit === 'both' ? `${kg} (${lb})` : lb
}

/**
 * The enrollment door's PioneerRx banner, which shows both units: the pharmacy's unit first (kg first under both).
 * Both figures are converted from the stored ounces with the exact factors in every mode. Owner ruling 2026-10-22:
 * the lb-mode banner's kg part moved off the older 0.0283495 factor, accepting a 0.1 kg change for a few weights.
 */
export function formatWeightBanner(oz: number, unit: WeightUnit): string {
  return unit === 'lb'
    ? `${formatWeight(oz, 'lb')} / ${formatWeight(oz, 'kg')}`
    : `${formatWeight(oz, 'kg')} / ${formatWeight(oz, 'lb')}`
}

/**
 * The 'both' entry: two linked boxes, kg and lb. `typed` is the box the operator typed in last — its text stays as
 * typed and is the value saved; the other box shows that value's stored ounces in its own unit, so it fills live and
 * shows what will be stored. Untouched: both boxes show the seeded ounces. Re-typing the seeded value in either box
 * keeps the seeded ounces (no drift). Blank or out-of-range typed text leaves the other box blank.
 */
export function linkedWeightBoxes(
  typed: { text: string; unit: WeightBoxUnit } | null, seededOz: number | null | undefined,
): { kg: string; lb: string; parsed: Parsed } {
  if (!typed) {
    const seed = seededOz && seededOz > 0 ? seededOz : null
    return { kg: kilogramsFromOunces(seed), lb: poundsFromOunces(seed), parsed: { ok: true, value: seed } }
  }
  let parsed = ouncesFromWeightBox(typed.text, typed.unit, seededOz)
  if (typed.unit === 'lb' && parsed.ok && parsed.value !== null && seededOz && seededOz > 0
      && Number(typed.text) === Number(poundsFromOunces(seededOz)))
    parsed = { ok: true, value: seededOz }   // lb re-typed as seeded: keep the ounces (lb mode itself is unchanged)
  const other = parsed.ok && parsed.value !== null ? parsed.value : null
  return typed.unit === 'kg'
    ? { kg: typed.text, lb: poundsFromOunces(other), parsed }
    : { kg: kilogramsFromOunces(other), lb: typed.text, parsed }
}
